import React, { useEffect, useState } from 'react';
import { Button, Modal, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import { message } from 'antd';

function readFileAsDataURL( file ) {
    return new Promise( ( resolve, reject ) => {
        const reader = new FileReader();
        reader.onload = e => resolve( e.target.result );
        reader.onerror = reject;
        reader.readAsDataURL( file );
    } );
}

function createImage( url ) {
    return new Promise( ( resolve, reject ) => {
        const image = new Image();
        image.addEventListener( 'load', () => resolve( image ) );
        image.addEventListener( 'error', reject );
        image.src = url;
    } );
}

async function getCroppedImg( imageSrc, pixelCrop, cropFileName ) {
    const image = await createImage( imageSrc );
    const canvas = document.createElement( 'canvas' );
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext( '2d' );
    ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
    );
    const mimetype = cropFileName.match( /\.jpe?g$/ ) ? 'image/jpeg' : 'image/png';
    return canvas.toDataURL( mimetype );
}

async function dataURLToFile( dataUrl, name ) {
    const blob = await ( await fetch( dataUrl ) ).blob();
    return new File( [ blob ], name || 'image.png', { type: blob.type } );
}

const ImageCropUpload = ( {
    value,
    onChange,
    onFileChange,
    onDirty,
    disabled,
    aspect = 1,
    title = 'Crop Image',
    uploadText = 'Upload',
    name = 'Image',
    cropHeight = 400,
    pngOnly = false
} ) => {

    const [ thumbnailList, setThumbnailList ] = useState( [] );

    const [ cropOpen, setCropOpen ] = useState( false );
    const [ cropImageSrc, setCropImageSrc ] = useState( null );
    const [ cropFileName, setCropFileName ] = useState( null );
    const [ crop, setCrop ] = useState( { x: 0, y: 0 } );
    const [ zoom, setZoom ] = useState( 1 );
    const [ croppedAreaPixels, setCroppedAreaPixels ] = useState( null );

    useEffect( () => {
        setThumbnailList( value ? [ { thumbUrl: value, name } ] : [] );
    }, [ value, name ] );

    function beforeUpload( file ) {
        let isValid = true;

        if ( pngOnly ) {
            isValid = file.type === 'image/png';
            if ( !isValid ) {
                message.error( `${ file.name } is not a png file` );
            }
        }

        return isValid || Upload.LIST_IGNORE;
    }

    async function handleUpload( info ) {
        if ( info.file.status === 'removed' || info.fileList.length === 0 ) {
            return;
        }


        const fileObj = info?.file?.originFileObj || info?.file;
        if ( !fileObj || typeof fileObj === 'string' ) {
            return;
        }

        const dataUrl = await readFileAsDataURL( fileObj );
        setCropImageSrc( dataUrl );
        setCropFileName( fileObj.name );
        setCrop( { x: 0, y: 0 } );
        setZoom( 1 );
        setCroppedAreaPixels( null );
        setCropOpen( true );
    }

    function resetCropState() {
        setCropOpen( false );
        setCropImageSrc( null );
        setCropFileName( null );
        setCroppedAreaPixels( null );
        setCrop( { x: 0, y: 0 } );
        setZoom( 1 );
    }

    async function handleCropOk() {
        if ( !cropImageSrc || !croppedAreaPixels ) {
            resetCropState();
            return;
        }
        try {
            const dataUrl = await getCroppedImg( cropImageSrc, croppedAreaPixels, cropFileName );
            const file = await dataURLToFile( dataUrl, cropFileName );
            setThumbnailList( [ {
                name: cropFileName || name,
                status: 'done',
                thumbUrl: dataUrl,
                file,
            } ] );
            onChange?.( dataUrl );
            onFileChange?.( file );
            onDirty?.();
        } catch ( e ) {
            console.error( e );
        } finally {
            resetCropState();
        }
    }

    function handleRemove() {
        setThumbnailList( [] );
        onChange?.( null );
        onFileChange?.( null );
        onDirty?.();
    }

    return <>
        <Upload
            className='upload-list'
            accept={ pngOnly ? 'image/png' : 'image/*' }
            style={ { height: '200px', width: '200px', margin: '20px 0' } }
            listType='picture-card'
            disabled={ disabled }
            onChange={ handleUpload }
            onRemove={ handleRemove }
            beforeUpload={ beforeUpload }
            fileList={ thumbnailList }
        >
            { !( thumbnailList?.length > 0 ) &&
                <Button type='primary' icon={ <UploadOutlined /> } disabled={ disabled }>
                    { uploadText }
                </Button>
            }
        </Upload>
        <Modal
            title={ title }
            open={ cropOpen }
            onOk={ handleCropOk }
            onCancel={ resetCropState }
            okText='Save'
            cancelText='Cancel'
            destroyOnHidden
            maskClosable={ false }
        >
            <div style={ { position: 'relative', width: '100%', height: cropHeight, background: '#333' } }>
                { cropImageSrc && <Cropper
                    image={ cropImageSrc }
                    crop={ crop }
                    zoom={ zoom }
                    aspect={ aspect }
                    onCropChange={ setCrop }
                    onZoomChange={ setZoom }
                    onCropComplete={ ( _, area ) => setCroppedAreaPixels( area ) }
                /> }
            </div>
        </Modal>
    </>;
};

export default ImageCropUpload;
